/**
 * Manage Capture API
 * 
 * This module handles API calls for starting/stopping capture using AWS Step Functions.
 * It provides a clean interface for the frontend to interact with the capture functionality.
 */

import ApiConfig from "../services/ApiConfig";
import axios from "axios";
import environmentMapping from "../config/environmentMapping.json";

/**
 * Start or stop capture by invoking the appropriate Step Function
 * 
 * @param {string|string[]} eventId - The event ID or array of event IDs
 * @param {string} environment - The environment (short code or full name)
 * @param {string} capture - The capture state ("on" or "off")
 * @returns {Promise<Object>} Response object with success/error information
 */
const manageCaptureApi = async (eventId, environment, capture) => {
    try {
        // Validate inputs
        if (!eventId || !environment || !capture) {
            throw new Error("Invalid parameters: eventId, environment, and capture are required");
        }

        if (capture !== "on" && capture !== "off") {
            throw new Error("Invalid capture value: capture must be 'on' or 'off'");
        }

        // Ensure eventId is an array
        const eventIdList = Array.isArray(eventId) ? eventId : [eventId];

        // Map short environment codes to full names
        const fullEnvironmentName = environmentMapping[environment] || environment;
        
        // Get API configuration
        var config = await ApiConfig();
        config.method = "post";
        config.url += "/manage_capture";
        
        // Prepare request payload
        config.data = {
            environment: fullEnvironmentName,
            eventIdList: eventIdList,
            capture: capture
        };
        
        // Make the API call
        return await axios(config)
            .then(function (response) {
                return response.data;
            })
            .catch(function (error) {
                // Handle different types of errors
                if (error.response) {
                    // Server responded with error status
                    const status = error.response.status;
                    const data = error.response.data;
                    
                    // Handle specific error cases
                    if (status === 409 && data && data.alreadyExists) {
                        return {
                            message: `Capture ${capture} already in progress for this event`,
                            alreadyExists: true,
                            executionArn: data.executionArn || null
                        };
                    } else if (status === 403) {
                        throw new Error("Access denied to Step Function. Please check IAM permissions or contact the administrator.");
                    } else if (status === 500) {
                        // 500 can be infrastructure errors (like missing cross-account role) or server errors
                        const message = data?.message || data?.error || "An internal server error occurred. Please try again or contact support.";
                        throw new Error(message);
                    } else if (status === 400) {
                        // Generic bad request message
                        const message = data?.message || data?.error || "Invalid request. Please check your input and try again.";
                        throw new Error(message);
                    } else if (data && data.message) {
                        throw new Error(data.message);
                    } else {
                        throw new Error(`Server error (${status}). Please try again or contact support.`);
                    }
                } else if (error.request) {
                    // Request was made but no response received (network error)
                    throw new Error("Network error. Please check your connection and try again.");
                } else {
                    // Something else happened (error in request setup)
                    throw new Error(error.message || "An unexpected error occurred. Please try again.");
                }
            });
    } catch (error) {
        console.error("Error in manageCaptureApi:", error);
        throw error;
    }
};

export default manageCaptureApi;
